import {
    useCombobox,
    ComboboxLikeProps,
    __BaseInputProps,
    Pill,
    Combobox,
    PillsInput,
    getParsedComboboxData,
    getOptionsLockup,
    OptionsDropdown,
    ScrollAreaProps,
    BoxProps,
    ElementProps,
    Text,
    ComboboxLikeRenderOptionInput,
    ComboboxItem,
    Group,
    Checkbox,
    ComboboxHiddenInputProps,
    Box,
} from '@mantine/core';
import styles from '../styles/Input.module.css';
import menuStyles from '../styles/DropdownMenu.module.css';
import { IconChevronDown } from '@tabler/icons-react';
import { useId } from 'react';
import { useUncontrolled } from '@mantine/hooks';

export type MultiSelectProps = {
    maxDisplayedValues?: number;
    value?: string[];
    defaultValue?: string[];
    searchValue?: string;
    defaultSearchValue?: string;
    onChange?: (value: string[]) => void;
    onSearchChange?: (value: string) => void;
    placeholder?: string;
    searchable?: boolean;
    scrollAreaProps?: ScrollAreaProps;
    maxValues?: number;
    nothingFoundMessage?: string;
    hiddenInputProps?: ComboboxHiddenInputProps;
} & BoxProps &
    ComboboxLikeProps &
    __BaseInputProps &
    ElementProps<'input', 'size' | 'value' | 'defaultValue' | 'onChange'>;

export const MultiSelect = ({
    maxDisplayedValues = Infinity,
    value,
    defaultValue,
    onChange,
    searchable,
    placeholder,
    data,
    maxValues = Infinity,
    rightSection = <IconChevronDown size={16} />,
    searchValue,
    defaultSearchValue,
    onSearchChange,
    disabled,
    size,
    comboboxProps,
    label,
    description,
    error,
    required,
    withAsterisk,
    withErrorStyles,
    filter,
    limit,
    withScrollArea,
    scrollAreaProps,
    maxDropdownHeight,
    nothingFoundMessage,
    form,
    hiddenInputProps,
}: MultiSelectProps) => {
    const _id = useId();

    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
        onDropdownOpen: () => combobox.updateSelectedOptionIndex('active'),
    });

    const parsedData = getParsedComboboxData(data);
    const optionsLockup = getOptionsLockup(parsedData);

    const [_value, setValue] = useUncontrolled({
        value,
        defaultValue,
        finalValue: [],
        onChange,
    });
    const [_searchValue, setSearchValue] = useUncontrolled({
        value: searchValue,
        defaultValue: defaultSearchValue,
        finalValue: '',
        onChange: onSearchChange,
    });

    const handleSearchChange = (value: string) => {
        setSearchValue(value);
        combobox.resetSelectedOption();
    };

    const values = _value.slice(0, maxDisplayedValues).map(item => (
        <Pill
            key={item}
            withRemoveButton
            onRemove={() => setValue(_value.filter(i => item !== i))}
            className={styles.pill}
            disabled={disabled}
        >
            {optionsLockup[item]?.label || item}
        </Pill>
    ));

    const renderOption = ({ option, checked }: ComboboxLikeRenderOptionInput<ComboboxItem>) => {
        return (
            <Group>
                <Checkbox checked={checked} id={'React'} readOnly aria-readonly /> <Text>{option.label}</Text>
            </Group>
        );
    };

    return (
        <Combobox
            store={combobox}
            size={size}
            onOptionSubmit={val => {
                handleSearchChange('');
                combobox.updateSelectedOptionIndex('selected');
                if (_value.includes(optionsLockup[val]?.value ?? val)) {
                    setValue(_value.filter(v => v !== optionsLockup[val]?.value));
                } else if (_value.length < maxValues!) {
                    setValue([..._value, optionsLockup[val]?.value ?? val]);
                }
            }}
            classNames={{ option: menuStyles.option, dropdown: menuStyles.dropdown }}
            {...comboboxProps}
        >
            <Combobox.DropdownTarget>
                <PillsInput
                    onClick={() => (searchable ? combobox.openDropdown() : combobox.toggleDropdown())}
                    rightSection={rightSection}
                    rightSectionPointerEvents="none"
                    id={_id}
                    label={label}
                    description={description}
                    error={error}
                    withErrorStyles={withErrorStyles}
                    classNames={{
                        input: styles.input,
                        label: styles.label,
                        description: styles.description,
                        error: styles.error,
                    }}
                    pointer={!searchable}
                    withAsterisk={withAsterisk}
                    required={required}
                    disabled={disabled}
                >
                    <Pill.Group disabled={disabled}>
                        {_value.length > 0 && (
                            <>
                                {values}
                                {_value.length > maxDisplayedValues && (
                                    <Text size="sm" c="gray.8">
                                        +{_value.length - maxDisplayedValues}
                                    </Text>
                                )}
                            </>
                        )}

                        <Combobox.EventsTarget>
                            <PillsInput.Field
                                type={!searchable && !placeholder ? 'hidden' : 'visible'}
                                placeholder={_value.length === 0 ? placeholder : ''}
                                value={_searchValue}
                                onFocus={() => {
                                    if (searchable) combobox.openDropdown();
                                }}
                                onBlur={() => {
                                    combobox.closeDropdown();
                                    handleSearchChange('');
                                }}
                                onChange={event => {
                                    handleSearchChange(event.currentTarget.value);
                                    if (searchable) combobox.openDropdown();
                                }}
                                onKeyDown={event => {
                                    if (event.key === ' ' && !searchable) {
                                        event.preventDefault();
                                        combobox.toggleDropdown();
                                    }

                                    if (
                                        event.key === 'Backspace' &&
                                        _searchValue.length === 0 &&
                                        _value.length > 0
                                    ) {
                                        setValue(_value.slice(0, _value.length - 1));
                                    }
                                }}
                                disabled={disabled}
                                pointer={!searchable}
                                readOnly={!searchable}
                            />
                        </Combobox.EventsTarget>
                    </Pill.Group>
                </PillsInput>
            </Combobox.DropdownTarget>
            <OptionsDropdown
                hidden={disabled}
                data={parsedData}
                filter={filter}
                search={_searchValue}
                limit={limit}
                withScrollArea={withScrollArea}
                maxDropdownHeight={maxDropdownHeight}
                filterOptions={searchable}
                labelId={label ? `${_id}-label` : undefined}
                aria-label={label ? String(label) : 'multiselect'}
                unstyled
                scrollAreaProps={scrollAreaProps}
                value={_value}
                nothingFoundMessage={<Box component={'option'}>{nothingFoundMessage}</Box>}
                hiddenWhenEmpty={!nothingFoundMessage}
                renderOption={renderOption}
            />
            <Combobox.HiddenInput value={_value} form={form} disabled={disabled} {...hiddenInputProps} />
        </Combobox>
    );
};
